import { z } from 'zod';
import { compiledPackageRefSchema } from '../macroPackage/packageRun';
import { sha256HexSchema } from '../macroPackage/canonicalJson';
import { graphAssetSchema, graphCountSchema, graphDiagnosticSchema, graphIdSchema } from './primitives';
import { graphRunPolicySchema, macroGraphDocumentSchema } from './source';
import { externalCallResultSchema, nodeContextSchema } from './rules';
import { graphRunEventSchema, graphRunSnapshotSchema } from './run';

const identity = { version: z.literal(1), requestId: graphIdSchema, targetSessionId: graphIdSchema };
const errorCode = z.string().regex(/^[a-z0-9_]{1,128}$/).nullable();
export const LOCAL_MACRO_GRAPH_PATH = '/macro-editor/graphs' as const;

/** Trusted Host control only. An editor cannot bind another interactive target. */
export const graphSessionCommandSchema = z.discriminatedUnion('command', [
  z.object({ ...identity, command: z.literal('bind'), captureTarget: z.enum(['selected', 'process']) }).strict(),
  z.object({ ...identity, command: z.literal('revoke') }).strict(),
  z.object({ ...identity, command: z.literal('cleanup-previous-target'), previousTargetSessionId: graphIdSchema,
    graphRunId: graphIdSchema.nullable() }).strict(),
]).superRefine((command, context) => {
  if (command.command === 'cleanup-previous-target' && command.previousTargetSessionId === command.targetSessionId) context.addIssue({
    code: z.ZodIssueCode.custom, path: ['previousTargetSessionId'], message: 'Previous-target cleanup cannot name the current target' });
});
export const graphHostCapabilitiesSchema = z.object({ mode: z.enum(['development', 'release']), allowDevelopmentPowerShell: z.boolean(),
  nodeEvaluation: graphRunPolicySchema.shape.nodeEvaluation, maxConcurrentEvaluations: z.number().int().positive().safe(),
  diagnosticsVersion: z.literal(1).optional() }).strict();
export type GraphHostCapabilities = z.infer<typeof graphHostCapabilitiesSchema>;
export const graphSessionReplySchema = z.object({ ...identity, hostEpoch: graphIdSchema.nullable(),
  capabilities: graphHostCapabilitiesSchema.nullable(), errorCode }).strict();
export type GraphSessionCommand = z.infer<typeof graphSessionCommandSchema>;
export type GraphSessionReply = z.infer<typeof graphSessionReplySchema>;

/** Main-process staging or authenticated campaign publication mints this registration. */
export const graphRegistrationCommandSchema = z.discriminatedUnion('command', [
  z.object({ ...identity, command: z.literal('register'), registrationId: graphIdSchema,
    graphSha256: sha256HexSchema, artifact: compiledPackageRefSchema, assets: z.array(graphAssetSchema),
    source: macroGraphDocumentSchema.optional() }).strict(),
  z.object({ ...identity, command: z.literal('revoke-registration'), registrationId: graphIdSchema }).strict(),
]);
export const graphRegistrationReplySchema = z.object({ ...identity, registrationId: graphIdSchema, errorCode }).strict();
export type GraphRegistrationCommand = z.infer<typeof graphRegistrationCommandSchema>;
export type GraphRegistrationReply = z.infer<typeof graphRegistrationReplySchema>;

export const graphTraceHistoryRequestSchema = z.object({ ...identity, graphRunId: graphIdSchema,
  cursor: z.string().min(1).nullable(), limit: graphCountSchema.refine(count => count > 0) }).strict();
export const graphTraceHistoryReplySchema = z.object({ ...identity, graphRunId: graphIdSchema,
  events: z.array(graphRunEventSchema), nextCursor: z.string().min(1).nullable(), errorCode }).strict().superRefine((reply, context) => {
  if (reply.events.some(event => event.graphRunId !== reply.graphRunId || event.targetSessionId !== reply.targetSessionId)
    || (reply.errorCode !== null && (reply.events.length > 0 || reply.nextCursor !== null))) context.addIssue({
    code: z.ZodIssueCode.custom, message: 'History must contain only owned public events or an error' });
});
export type GraphTraceHistoryRequest = z.infer<typeof graphTraceHistoryRequestSchema>;
export type GraphTraceHistoryReply = z.infer<typeof graphTraceHistoryReplySchema>;

export const graphSimulationCommandSchema = z.discriminatedUnion('command', [
  z.object({ ...identity, command: z.literal('simulate'), simulationId: graphIdSchema, source: macroGraphDocumentSchema,
    contexts: z.array(nodeContextSchema), attachmentResults: z.array(z.object({ nodeId: graphIdSchema, receipt: externalCallResultSchema }).strict()) }).strict(),
  z.object({ ...identity, command: z.literal('cancel-simulation'), simulationId: graphIdSchema }).strict(),
]).superRefine((command, context) => {
  if (command.command === 'simulate' && command.contexts.some(node => node.environment.targetSessionId !== command.targetSessionId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Simulation contexts must belong to the selected session' });
  }
});
export const graphSimulationReplySchema = z.object({ ...identity, simulationId: graphIdSchema,
  events: z.array(graphRunEventSchema), diagnostics: z.array(graphDiagnosticSchema), errorCode }).strict().superRefine((reply, context) => {
  if (reply.events.some(event => event.targetSessionId !== reply.targetSessionId) || (reply.errorCode !== null && reply.events.length > 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Simulation must contain only owned public events or an error' });
  }
});
export type GraphSimulationCommand = z.infer<typeof graphSimulationCommandSchema>;
export type GraphSimulationReply = z.infer<typeof graphSimulationReplySchema>;

export const graphRunReplySchema = z.object({ requestId: graphIdSchema, snapshot: graphRunSnapshotSchema.nullable(), errorCode }).strict()
  .superRefine((reply, context) => {
    if ((reply.snapshot === null) === (reply.errorCode === null)) context.addIssue({ code: z.ZodIssueCode.custom,
      message: 'Graph reply must contain a snapshot or an error' });
  });
export type GraphRunReply = z.infer<typeof graphRunReplySchema>;
